import { Activity, CalendarCheck, CalendarDays, ChartColumn, ChessBishop, ChessKing, ChessKnight, ChessPawn, ChessQueen, ChessRook, Drama, Hospital, Mail, Pencil, Phone, Power, Puzzle, SlidersHorizontal, Users, Wrench, type LucideIcon, TrendingUp } from 'lucide-react'

/**
 * The app's named icons, drawn by lucide-react. Callers ask for what the
 * icon means ("a facility"), not which drawing it is, so a concept keeps
 * one picture everywhere and can be changed here alone. Each takes its
 * colour from the surrounding text and its size from `className`.
 */
interface IconProps {
  className?: string
}

function named(Icon: LucideIcon, defaultClassName: string) {
  return function NamedIcon({ className = defaultClassName }: IconProps) {
    return <Icon aria-hidden="true" strokeWidth={1.75} className={`${className} shrink-0`} />
  }
}

export const FacilityIcon = named(Hospital, 'w-5 h-5')
export const WorkersIcon = named(Users, 'w-3.5 h-3.5')
export const MailIcon = named(Mail, 'w-3.5 h-3.5')
export const PhoneIcon = named(Phone, 'w-3.5 h-3.5')
export const EditIcon = named(Pencil, 'w-3.5 h-3.5')
/** Signing out: the power symbol, shown on its own with no word beside it, so the drawing has to say it. */
export const SignOutIcon = named(Power, 'w-4 h-4')
/** Playing a part: opening a demo client as one of its people, or switching who a demo session is. */
export const DemoRoleIcon = named(Drama, 'w-4 h-4')

// One chess piece per kind of person, the same ones the admin menu and the
// shells' headers use, so who someone is reads from the icon before the
// name. The pieces follow reach: the king is the one root admin, the queen
// an admin with the same reach over every client, the rook an operator
// with a whole client, the bishop a facility administrator working along
// their own facilities, the knight a worker. No pawn: nobody here is one.
export const RootAdminIcon = named(ChessKing, 'w-4 h-4')
export const AdminIcon = named(ChessQueen, 'w-4 h-4')
/** A sales login: the piece with the shortest reach - its own demos and nothing else. */
export const SalesIcon = named(ChessPawn, 'w-4 h-4')
export const OperatorIcon = named(ChessRook, 'w-4 h-4')
export const FacilityAdministratorIcon = named(ChessBishop, 'w-4 h-4')
/** A single worker. `WorkersIcon` is for a count of them. */
export const WorkerIcon = named(ChessKnight, 'w-4 h-4')

/** The lucide drawings behind the role icons, for places that take an icon component rather than render one (the admin menu). */
export const ROLE_PIECES = { root: ChessKing, admin: ChessQueen, operator: ChessRook, facility_administrator: ChessBishop, worker: ChessKnight } as const satisfies Record<string, LucideIcon>

/**
 * The icon of each section of the shell's bars (the Dashboard's bar, the facility list's, an add-on's tab): the one
 * place a section's picture is chosen, so replacing one is a line here. The mockup's bar draws the same set.
 */
export const NAV_ICONS = {
  today: CalendarCheck,
  month: CalendarDays,
  roster: Users,
  activity: Activity,
  reporting: ChartColumn,
  tools: Wrench,
  configuration: SlidersHorizontal,
  facilities: Hospital,
  addOn: Puzzle,
  insights: TrendingUp,
} as const satisfies Record<string, LucideIcon>

export type NavSection = keyof typeof NAV_ICONS

/** A section's icon from `NAV_ICONS`, sized for a bar's tab and coloured by its text. */
export function NavIcon({ section, className = 'w-[15px] h-[15px]' }: { section: NavSection; className?: string }) {
  const Icon = NAV_ICONS[section]
  return <Icon aria-hidden="true" strokeWidth={2} className={`${className} shrink-0`} />
}
